import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/var/www/html/src/components/Layout/Tutorials.tsx";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1>{`Implementing SMS two-factor authentication (2FA) with MessageBird`}</h1>
    <h3>{`⏱ 15 min build time || `}<a parentName="h3" {...{
        "href": "https://github.com/messagebirdguides/verify-guide"
      }}>{`Download the Code`}</a></h3>
    <h2>{`Why build two-factor authentication?`}</h2>
    <p>{`In this MessageBird Developer Tutorial you’ll learn how to improve your security building an SMS-based two-factor authentication solution with the `}<a parentName="p" {...{
        "href": "https://www.messagebird.com/en/verify"
      }}>{`MessageBird Verify API`}</a>{`. The runnable application we’ll build is a prototype in Node.js for our fictitious online banking application, `}<em parentName="p">{`BirdBank`}</em>{`.`}</p>
    <p>{`Enterprises are increasingly challenged to keep sensitive information from falling into the wrong hands. This means that we can no longer trust old online authentication systems that rely solely on usernames and passwords, especially as security breaches grow in frequency, severity and sophistication.`}</p>
    <p>{`With the `}<a parentName="p" {...{
        "href": "https://www.messagebird.com/en/verify"
      }}>{`MessageBird Verify API`}</a>{`, you can implement two-factor authentication (2FA) solutions to provide an additional layer of account security by verifying the user's password with a second authentication token and in turn, secure customer data, block fraudulent accounts and safeguard key transactions in a matter of minutes. The most common use case involves the application of one-time passwords (OTP) generated by hardware tokens, authenticator apps or directly sent to the user's mobile phone via SMS messaging.`}</p>
    <p>{`We'll walk you through the following steps:`}</p>
    <ul>
      <li parentName="ul">{`Asking for the phone number`}</li>
      <li parentName="ul">{`Sending a verification code`}</li>
      <li parentName="ul">{`Verifying the code`}</li>
    </ul>
    <p><strong parentName="p">{`Pro-tip:`}</strong>{` Follow this tutorial to build the whole application from scratch or, if you want to see it in action right away, you can download, clone or fork the sample application from the `}<a parentName="p" {...{
        "href": "https://github.com/messagebirdguides/verify-guide"
      }}>{`MessageBird Developer Tutorials GitHub repository`}</a>{`.`}</p>
    <h2>{`Getting started`}</h2>
    <p>{`To build our sample application we'll use `}<a parentName="p" {...{
        "href": "https://nodejs.org/en/"
      }}>{`Node.js`}</a>{`, the `}<a parentName="p" {...{
        "href": "https://github.com/expressjs/express"
      }}>{`Express framework`}</a>{`, the `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/package/express-handlebars"
      }}>{`Handlebars templating engine`}</a>{` as well as the `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/package/messagebird"
      }}>{`MessageBird SDK`}</a>{`.`}</p>
    <p>{`Before we get started, make sure Node's package manager (npm) is installed. If not, you can easily `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/get-npm"
      }}>{`download it for free`}</a>{`.`}</p>
    <h2>{`Project setup`}</h2>
    <h3>{`Dependencies`}</h3>
    <p>{`First, let's create a new directory to store the sample application. Within this new directory we'll create a file called `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` with the following content:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "name": "node-messagebird-verify-example",
  "main": "index.js",
  "dependencies": {
    "body-parser": "^1.18.3",
    "dotenv": "^5.0.1",
    "express": "^4.16.3",
    "express-handlebars": "^3.0.0",
    "messagebird": "^2.1.4"
  }
}
`}</code></pre>
    <p>{`This file provides a `}<strong parentName="p">{`name`}</strong>{` for your sample application, declares the main (which we'll create next) and lists the `}<strong parentName="p">{`dependencies`}</strong>{` you'll need along with their versions. Apart from Express, Handlebars and the MessageBird SDK, we're adding a small helper library called `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/package/body-parser"
      }}>{`body-parser`}</a>{` to help simplify your code.`}</p>
    <p>{`Next, we need to instruct npm to install all the required modules in your project. Let's open a console pointed to the directory that contains the file you just created and type the following command:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm install
`}</code></pre>
    <h4>{`Create your API Key 🔑`}</h4>
    <p>{`To enable the MessageBird SDK, we need to provide an access key for the API. MessageBird provides keys in `}<em parentName="p">{`live`}</em>{` and `}<em parentName="p">{`test`}</em>{` modes. To get this application running, we’ll need to create and use a live API access key. You can read more about `}<a parentName="p" {...{
        "href": "https://support.messagebird.com/hc/en-us/articles/360000670709-What-is-the-difference-between-a-live-key-and-a-test-key-"
      }}>{`the difference between test and live API keys`}</a>{` in our Help Center.`}</p>
    <p>{`Let's create your live API access key. First, go to the `}<a parentName="p" {...{
        "href": "https://dashboard.messagebird.com/en/user/index"
      }}>{`MessageBird Dashboard`}</a>{`; if you have already created an API key it will be shown right there. If you don’t see any key on the Dashboard or if you're unsure whether this key is in `}<em parentName="p">{`live`}</em>{` mode, go to the `}<em parentName="p">{`Developers`}</em>{` section in the MessageBird Dashboard and open the `}<a parentName="p" {...{
        "href": "https://dashboard.messagebird.com/en/developers/access"
      }}>{`API access (REST) tab`}</a>{`. There you can create new API keys and manage your existing ones.`}</p>
    <p>{`If you’re having any issues creating your API key, please reach out to `}<a parentName="p" {...{
        "href": "mailto:support@messagebird.com"
      }}>{`support@messagebird.com`}</a>{`; we’ll make sure to help you out.`}</p>
    <p><strong parentName="p">{`Pro-tip:`}</strong>{` Hardcoding your credentials is a risky practice that should never be used in production applications. A better method, also recommended by the `}<a parentName="p" {...{
        "href": "https://12factor.net/"
      }}>{`Twelve-Factor App Definition`}</a>{`, is to use environment variables.`}</p>
    <p>{`We've added `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/package/dotenv"
      }}>{`dotenv`}</a>{` to the sample application so that you can supply your API key in a file named `}<inlineCode parentName="p">{`.env`}</inlineCode>{`. You can copy the provided file `}<inlineCode parentName="p">{`env.example`}</inlineCode>{` to `}<inlineCode parentName="p">{`.env`}</inlineCode>{` and add your API key like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-env"
      }}>{`MESSAGEBIRD_API_KEY=YOUR-API-KEY
`}</code></pre>
    <h4>{`Main file`}</h4>
    <p>{`You now have your API key, so let's get started with the main file. First, create an `}<inlineCode parentName="p">{`index.js`}</inlineCode>{` file in the same directory as your `}<inlineCode parentName="p">{`package.json`}</inlineCode>{`. This file starts by including the dependencies:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var express = require('express');
var exphbs = require('express-handlebars');
var bodyParser = require('body-parser');
`}</code></pre>
    <p>{`Then, we initialize dotenv to load the API key from the `}<inlineCode parentName="p">{`.env`}</inlineCode>{` file:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`require('dotenv').config();
`}</code></pre>
    <p>{`Next, append the following line to your `}<inlineCode parentName="p">{`index.js`}</inlineCode>{` to include and set up the SDK:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var messagebird = require('messagebird')(process.env.MESSAGEBIRD_API_KEY);
`}</code></pre>
    <p>{`Now, let's initialize and configure the framework and enable Handlebars and the body parser:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`var app = express();
app.engine('handlebars', exphbs({defaultLayout: 'main'}));
app.set('view engine', 'handlebars');
app.use(bodyParser.urlencoded({ extended : true }));
`}</code></pre>
    <h4>{`Views`}</h4>
    <p>{`We use Handlebars to separate the logic of our code from the HTML pages. To do this, we create a directory named `}<inlineCode parentName="p">{`views`}</inlineCode>{`; inside `}<inlineCode parentName="p">{`views`}</inlineCode>{`, also we create another directory named `}<inlineCode parentName="p">{`layouts`}</inlineCode>{`, and a file called `}<inlineCode parentName="p">{`main.handlebars`}</inlineCode>{` inside with the following content:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<!DOCTYPE html>
<html>
  <head>
    <title>MessageBird Verify Example</title>
  </head>
  <body>
    <h1>MessageBird Verify Example</h1>

    {{{body}}}
  </body>
</html>
`}</code></pre>
    <p>{`This is the main layout which acts as a container for all pages of our application. We'll create the views for each page next.`}</p>
    <h2>{`Asking for the phone number`}</h2>
    <p>{`The first step in verifying a user's phone number is asking them to provide their phone number. Let's do exactly this by creating an HTML form and storing it as `}<inlineCode parentName="p">{`step1.handlebars`}</inlineCode>{` inside the `}<inlineCode parentName="p">{`views`}</inlineCode>{` directory:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`{{#if error}}
<p>{{error}}</p>
{{/if}}
<p>Please enter your phone number (in international format, starting with +) to receive a verification code:</p>
<form method="post" action="/step2">
  <input type="tel" name="number" />
  <input type="submit" value="Send code" />
</form>
`}</code></pre>
    <p>{`The form is simple, having just one input field and one submit button. Providing `}<inlineCode parentName="p">{`tel`}</inlineCode>{` as the `}<inlineCode parentName="p">{`type`}</inlineCode>{` attribute of our input field allows some browsers, especially on mobile devices, to optimize for telephone number input, for example, by displaying a number pad-style keyboard. The section starting with `}<inlineCode parentName="p">{`{{#if error}`}</inlineCode>{` is needed to display errors. We'll come back to this in a minute.`}</p>
    <p>{`Now, it's time to add a route to your `}<inlineCode parentName="p">{`index.js`}</inlineCode>{` to display the page:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`app.get('/', function(req, res) {
  res.render('step1');
});
`}</code></pre>
    <h2>{`Sending a verification code`}</h2>
    <p>{`Once we've collected the number, we can send a verification message to a user's mobile device. The MessageBird Verify API takes care of generating a random token, so you don't have to do this yourself. Codes are numeric and six digits by default. If you want to customize the length of the code or configure other options, you can check out our `}<a parentName="p" {...{
        "href": "/api/verify#verify-request"
      }}>{`Verify API documentation`}</a>{`.`}</p>
    <p>{`The form we created in the last step submits the phone number via HTTP POST to `}<inlineCode parentName="p">{`/step2`}</inlineCode>{`, so let's define this route in our `}<inlineCode parentName="p">{`index.js`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`app.post('/step2', function(req, res) {
   var number = req.body.number;
   messagebird.verify.create(number, {
       originator : 'Code',
       template : 'Your verification code is %token.'
   }, function (err, response) {
       if (err) {
           console.log(err);
           res.render('step1', {
               error : err.errors[0].description
           });
       } else {
           console.log(response);
           res.render('step2', {
               id : response.id
           });
       }
   })
});
`}</code></pre>
    <p>{`Before we move on, let's quickly dive into what happens here: 🤔`}</p>
    <p>{`First, we're creating a `}<inlineCode parentName="p">{`MessageBird\\Objects\\Verify`}</inlineCode>{` object to encapsulate the parameters for our API request. We set the number from the form as the `}<inlineCode parentName="p">{`recipient`}</inlineCode>{` attribute and specify a template for the message. The value for the `}<inlineCode parentName="p">{`template`}</inlineCode>{` attribute contains the placeholder `}<inlineCode parentName="p">{`%token`}</inlineCode>{`, which is replaced with the generated token on MessageBird's end. If we omitted this, the message would contain the token and nothing else.`}</p>
    <p>{`Also, using `}<inlineCode parentName="p">{`originator`}</inlineCode>{` we specify the sender ID of the text message with the code. The value should either be a valid telephone number `}<a parentName="p" {...{
        "href": "https://support.messagebird.com/hc/en-us/articles/115003950149-Numbers-format-"
      }}>{`international format with country code`}</a>{` , or an alphanumeric string with at most 11 characters. If we omitted this, it would default to the string `}<em parentName="p">{`Code`}</em>{`. Keep in mind that alphanumeric senders are not supported in every country including the United States, so it’s important to check the `}<a parentName="p" {...{
        "href": "https://support.messagebird.com/hc/en-us/sections/360000108538-Country-info-Restrictions"
      }}>{`country restrictions`}</a>{`.`}</p>
    <p>{`Like most JavaScript functions, the MessageBird API call is asynchronous and transfers control to a callback function once finished. In the SDK's case, this callback function takes two parameters, `}<inlineCode parentName="p">{`err`}</inlineCode>{` and `}<inlineCode parentName="p">{`response`}</inlineCode>{`.`}</p>
    <p>{`If `}<inlineCode parentName="p">{`err`}</inlineCode>{` is set, it means that an error has occurred. A typical error could be that the user has entered an invalid phone number. For our application, we simply re-render the page from our first step and pass the description of the error into the template. Remember the `}<inlineCode parentName="p">{`{{#if error}`}</inlineCode>{` section from the first step? In production applications, you'd most likely not expose the raw API error; instead, you could consider different possible problems and return an appropriate message in your own words. You might also want to prevent some errors from occurring by doing some input validation on the phone number yourself. We've also added a `}<inlineCode parentName="p">{`console.log(err)`}</inlineCode>{` statement so you can explore the complete error object in your console and learn more about how it works.`}</p>
    <p>{`In case the request was successful, we'll render a new page. Our API response contains an ID—which we'll need for the next step—so we'll just add it to the form. Since the ID is meaningless without your API access key, there are no security implications of doing so; however, in practice, you'd be more likely to store this ID in a session object on the server. Just as before, we're logging the whole response to the console for debugging purposes. We still need to build the new page, so create a file called `}<inlineCode parentName="p">{`step2.handlebars`}</inlineCode>{` in your `}<inlineCode parentName="p">{`views`}</inlineCode>{` directory:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`{{#if error}}
<p>{{error}}</p>
{{/if}}
<p>We have sent you a verification code!</p>
<p>Please enter the code here:</p>
<form method="post" action="/step3">
  <input type="hidden" name="id" value="{{id}}" />
  <input type="text" name="token" />
  <input type="submit" value="Check code" />
</form>
`}</code></pre>
    <p>{`The form is very similar to the first step. Keep in mind that we include a hidden field with our verification ID and, once again, have a conditional error section.`}</p>
    <h2>{`Verifying the code`}</h2>
    <p>{`Once the code is delivered, our user will check their phone, enter their verification code and submit the form. We now need to send the user's input along with the ID of the verification request to the MessageBird API and see whether the verification was successful or not. Let's declare this third step as a new route in our `}<inlineCode parentName="p">{`index.js`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`app.post('/step3', function(req, res) {
  var id = req.body.id;
  var token = req.body.token;
  messagebird.verify.verify(id, token, function(err, response) {
    if (err) {
      console.log(err);
      res.render('step2', {
        error: err.errors[0].description,
        id: id,
      });
    } else {
      console.log(response);
      res.render('step3');
    }
  });
});
`}</code></pre>
    <p>{`This code looks very similar to the one in the second step. First, we're reading the input and then make a call to the MessageBird API. This time, it's the `}<inlineCode parentName="p">{`messagebird.verify.verify()`}</inlineCode>{` method, which accepts `}<inlineCode parentName="p">{`id`}</inlineCode>{` and `}<inlineCode parentName="p">{`token`}</inlineCode>{` as its parameters. Inside the callback, error and success cases are handled.`}</p>
    <p>{`In case of an error, such as an invalid or expired token, we're showing that error on our page from the second step.`}</p>
    <p>{`In the success case, we simply show a new page. Create this page in your `}<inlineCode parentName="p">{`views`}</inlineCode>{` directory and call it `}<inlineCode parentName="p">{`step3.handlebars`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<p>You have successfully verified your phone number.</p>
`}</code></pre>
    <h2>{`Testing`}</h2>
    <p>{`Let's write one more line of code in your `}<inlineCode parentName="p">{`index.js`}</inlineCode>{` to run the Express application:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`app.listen(8080);
`}</code></pre>
    <p>{`Take a quick look at the directory structure you created. It should look something like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`node_modules
views
- layouts
- - main.handlebars
- step1.handlebars
- step2.handlebars
- step3.handlebars
index.js
package-lock.json
Package.json
`}</code></pre>
    <p>{`You’re done! Save your `}<inlineCode parentName="p">{`index.js`}</inlineCode>{` and run the application from the command line to test it:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`node index.js
`}</code></pre>
    <p>{`Then, point your browser to `}<a parentName="p" {...{
        "href": "http://localhost:8080/"
      }}>{`http://localhost:8080/`}</a>{` and try to verify your own phone number.`}</p>
    <p>{`Awesome! You can now leverage the flow, code snippets, and UI examples from this tutorial to build your own two factor authentication system. Don't forget to download the code from the `}<a parentName="p" {...{
        "href": "https://github.com/messagebirdguides/verify-guide"
      }}>{`MessageBird Developer Tutorials GitHub repository`}</a>{`.`}</p>
    <p><strong parentName="p">{`Nice work!`}</strong>{` 🎉`}</p>
    <p>{`You now have a running integration of MessageBird's Verify API using Node.js!`}</p>
    <h2>{`Start building!`}</h2>
    <p>{`Want to build something similar but not quite sure how to get started? Please feel free to let us know at `}<a parentName="p" {...{
        "href": "mailto:support@messagebird.com"
      }}>{`support@messagebird.com`}</a>{`; we'd love to help!`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      